import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './context/AuthContext';
import { apiGet, apiGetOwnershipXml, apiPost } from './api/client';
import LvSummaryAll from './components/LvSummaryAll';
import { usePdfLvSummary } from './hooks/usePdfLvSummary';
import { buildLvNumberPayload } from './utils/buildLvNumberPayload';
import { formatSavedPropertyLabel, isPdfBackedProperty } from './utils/propertyLabel';
import { fetchStoredLvPdf } from './utils/fetchStoredLvPdf';
import { useStoredLvPdfHydration } from './hooks/useStoredLvPdfHydration';
import { detectPropertyTypeFromSummary, PROPERTY_TYPE_VALUES } from './utils/propertyType';
import { DOCUMENT_KEYS, loadDocumentDraft, saveDocumentDraft } from './utils/documentDrafts';

const LAST_KU_LV_KEY = 'realty-cuzk-last-ku-lv-reservation';

function toOptionalString(v) {
  const s = (v ?? '').toString().trim();
  return s === '' ? '' : s;
}

function todayCsDate() {
  const d = new Date();
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
}

function blankBuyer() {
  return {
    buyerName: '',
    buyerLastName: '',
    buyerBirthDate: '',
    buyerPermanentAddress: '',
  };
}

export default function ReservationContractPage({
  onBack,
  onSetupRequired,
  initialProperty,
  isAdmin = false,
  handoffToken = null,
}) {
  const { t } = useTranslation();
  const { getToken } = useAuth();
  const selectedSourceUserId = initialProperty?.user_id ? String(initialProperty.user_id) : '';
  const [ku, setKu] = useState('');
  const [lv, setLv] = useState('');
  const [loadLoading, setLoadLoading] = useState(false);
  const [error, setError] = useState(null);
  const [xml, setXml] = useState(null);
  const [summary, setSummary] = useState(null);

  const [buyers, setBuyers] = useState([blankBuyer()]);
  const [floorText, setFloorText] = useState('');
  const [sellingPrice, setSellingPrice] = useState('');
  const [reservationFee, setReservationFee] = useState('');
  const [variableSymbol, setVariableSymbol] = useState('');
  const [ownFunds, setOwnFunds] = useState('');
  const [mortgageAmount, setMortgageAmount] = useState('');
  const [deadlineDate, setDeadlineDate] = useState('');
  const [todayDate, setTodayDate] = useState(todayCsDate());

  const [generateLoading, setGenerateLoading] = useState(false);
  const [generateError, setGenerateError] = useState(null);
  const [gdprLoading, setGdprLoading] = useState(false);
  const [gdprError, setGdprError] = useState(null);
  const [pdfBase64, setPdfBase64] = useState('');
  const [, setPdfName] = useState('');
  const [savedProperties, setSavedProperties] = useState([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState('');
  const [overrideDetectedType, setOverrideDetectedType] = useState(false);
  const [overrideType, setOverrideType] = useState(PROPERTY_TYPE_VALUES.LAND);
  const autoLoadKeyRef = useRef('');

  const { pdfSummary } = usePdfLvSummary(pdfBase64, getToken);
  const handoffInitRef = useRef('');

  useStoredLvPdfHydration(initialProperty, getToken, {
    setPdfBase64,
    setPdfName,
    setXml,
    setSummary,
    setError,
    setLoadLoading,
    translateError: () => t('reservationContract.errorFetch'),
    allowTargetUserId: isAdmin,
  });
  const [handoffLoading, setHandoffLoading] = useState(false);
  const [handoffContext, setHandoffContext] = useState(null);

  const kuTrim = (ku || '').trim();
  const lvTrim = (lv || '').trim();
  const canLoadLv = kuTrim.length > 0 && lvTrim.length > 0 && !loadLoading;
  const hasXml = typeof xml === 'string' && xml.trim().length > 0;
  const hasSource = hasXml || !!pdfBase64;
  const canGenerate = hasXml && !generateLoading;
  const detectedPropertyType = detectPropertyTypeFromSummary(summary || pdfSummary);

  const persistLastKuLv = useCallback((k, l) => {
    try {
      sessionStorage.setItem(LAST_KU_LV_KEY, JSON.stringify({ ku: k, lv: l }));
    } catch {
      // ignore
    }
  }, []);

  const hydrateDraftForKuLv = useCallback(async (k, l) => {
    try {
      const draft = await loadDocumentDraft(
        {
          kod_ku: k,
          cislo_lv: l,
          document_key: DOCUMENT_KEYS.RESERVATION_CONTRACT,
          user_id: selectedSourceUserId || undefined,
        },
        getToken,
      );
      const payload = draft?.payload;
      if (!payload || typeof payload !== 'object') return;
      const savedBuyers = Array.isArray(payload.buyers) && payload.buyers.length
        ? payload.buyers
        : [blankBuyer()];
      setBuyers(savedBuyers.map((buyer) => ({
        buyerName: buyer?.buyerName == null ? '' : String(buyer.buyerName),
        buyerLastName: buyer?.buyerLastName == null ? '' : String(buyer.buyerLastName),
        buyerBirthDate: buyer?.buyerBirthDate == null ? '' : String(buyer.buyerBirthDate),
        buyerPermanentAddress: buyer?.buyerPermanentAddress == null ? '' : String(buyer.buyerPermanentAddress),
      })));
      setFloorText(payload.floor == null ? '' : String(payload.floor));
      setSellingPrice(payload.sellingPrice == null ? '' : String(payload.sellingPrice));
      setReservationFee(payload.reservationFee == null ? '' : String(payload.reservationFee));
      setVariableSymbol(payload.variableSymbol == null ? '' : String(payload.variableSymbol));
      setOwnFunds(payload.ownFunds == null ? '' : String(payload.ownFunds));
      setMortgageAmount(payload.mortgageAmount == null ? '' : String(payload.mortgageAmount));
      setDeadlineDate(payload.deadlineDate == null ? '' : String(payload.deadlineDate));
      setTodayDate(payload.todayDate == null ? todayCsDate() : String(payload.todayDate));
      if (payload.propertyType) {
        setOverrideDetectedType(true);
        setOverrideType(String(payload.propertyType));
      }
    } catch (draftErr) {
      console.warn('reservationContract: failed to load saved draft', draftErr.message);
    }
  }, [getToken, selectedSourceUserId]);

  const loadXmlForKuLv = useCallback(async (kRaw, lRaw) => {
    const k = String(kRaw ?? '').trim();
    const l = String(lRaw ?? '').trim();
    if (!k || !l) return;
    setError(null);
    setLoadLoading(true);
    try {
      const { res, data } = await apiGetOwnershipXml({
        KodKU: k,
        CisloLv: l,
        userId: selectedSourceUserId || undefined,
        getToken,
        confirmMessage: t('lvWorkspace.refreshWarning'),
        allowPaidFetch: true,
      });
      if (res.status === 403 && data.code === 'SETUP_REQUIRED' && onSetupRequired) {
        onSetupRequired();
        return;
      }
      if (!res.ok) throw new Error(data.error || t('reservationContract.errorFetch'));
      if (!data.xml || typeof data.xml !== 'string') throw new Error(t('reservationContract.errorNoXml'));
      setXml(data.xml);
      setSummary(data.summary || null);
      setGenerateError(null);
      persistLastKuLv(k, l);
      await hydrateDraftForKuLv(k, l);
    } catch (err) {
      setError(err.message || t('reservationContract.errorUnknown'));
    } finally {
      setLoadLoading(false);
    }
  }, [getToken, hydrateDraftForKuLv, onSetupRequired, persistLastKuLv, selectedSourceUserId, t]);

  useEffect(() => {
    if (initialProperty) return;
    try {
      const raw = sessionStorage.getItem(LAST_KU_LV_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      const k = parsed?.ku != null ? String(parsed.ku).trim() : '';
      const l = parsed?.lv != null ? String(parsed.lv).trim() : '';
      if (k && l) {
        setKu(k);
        setLv(l);
      }
    } catch {
      // ignore
    }
  }, [initialProperty]);

  useEffect(() => {
    if (!initialProperty?.kod_ku || !initialProperty?.cislo_lv) {
      autoLoadKeyRef.current = '';
      return;
    }
    const k = String(initialProperty.kod_ku).trim();
    const l = String(initialProperty.cislo_lv).trim();
    if (!k || !l) return;
    const key = `${k}-${l}`;
    if (autoLoadKeyRef.current === key) return;
    autoLoadKeyRef.current = key;
    setKu(k);
    setLv(l);
    if (initialProperty.pdfBase64) return;
    const path = initialProperty.lv_pdf_storage_path || initialProperty.lvPdfStoragePath;
    if (path) return;
    loadXmlForKuLv(k, l);
  }, [initialProperty, loadXmlForKuLv]);

  useEffect(() => {
    if (!initialProperty?.pdfBase64) return;
    setPdfBase64(String(initialProperty.pdfBase64));
    setPdfName(String(initialProperty.pdfName || 'LV.pdf'));
    setXml(null);
    setSummary(null);
    setError(null);
  }, [initialProperty]);

  useEffect(() => {
    if (!initialProperty) return;
    const hasOverride = Boolean(initialProperty.property_type_override_enabled);
    const incomingType = String(initialProperty.property_type_override || '').trim();
    if (hasOverride && incomingType) {
      setOverrideDetectedType(true);
      setOverrideType(incomingType);
      return;
    }
    setOverrideDetectedType(false);
  }, [initialProperty]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiGet('/api/properties', getToken);
        const data = await res.json().catch(() => ({}));
        if (!cancelled && res.ok && Array.isArray(data.properties)) {
          setSavedProperties(data.properties);
        }
      } catch {
        if (!cancelled) setSavedProperties([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  useEffect(() => {
    const token = String(handoffToken || '').trim();
    if (!token) return;
    if (handoffInitRef.current === token) return;
    handoffInitRef.current = token;

    let cancelled = false;
    (async () => {
      setHandoffLoading(true);
      setError(null);
      try {
        const res = await apiPost('/api/exchange-reservation-handoff', { token }, getToken);
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data?.ok || !data?.handoff) {
          throw new Error(data?.error || 'Handoff exchange failed');
        }
        if (cancelled) return;
        setHandoffContext(data.handoff);

        // Remove sensitive token from URL after successful exchange.
        if (typeof window !== 'undefined') {
          const next = new URL(window.location.href);
          next.searchParams.delete('reservation_handoff');
          window.history.replaceState({}, '', `${next.pathname}${next.search}${next.hash}`);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err.message || 'Handoff exchange failed');
      } finally {
        if (!cancelled) setHandoffLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [handoffToken, getToken]);

  const updateBuyer = (idx, key, value) => {
    setBuyers((prev) => prev.map((b, i) => (i === idx ? { ...b, [key]: value } : b)));
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!canGenerate) return;
    setGenerateError(null);
    setGenerateLoading(true);
    try {
      const userInput = {
        docType: 'rezervacniSmlouva',
        buyers: buyers.map((b) => ({
          buyerName: toOptionalString(b.buyerName),
          buyerLastName: toOptionalString(b.buyerLastName),
          buyerBirthDate: toOptionalString(b.buyerBirthDate),
          buyerPermanentAddress: toOptionalString(b.buyerPermanentAddress),
        })),
        floor: toOptionalString(floorText),
        sellingPrice: toOptionalString(sellingPrice),
        reservationFee: toOptionalString(reservationFee),
        variableSymbol: toOptionalString(variableSymbol),
        ownFunds: toOptionalString(ownFunds),
        mortgageAmount: toOptionalString(mortgageAmount),
        deadlineDate: toOptionalString(deadlineDate),
        todayDate: toOptionalString(todayDate),
      };
      if (overrideDetectedType) {
        userInput.propertyType = overrideType;
      }

      try {
        await saveDocumentDraft(
          {
            kod_ku: kuTrim,
            cislo_lv: lvTrim,
            document_key: DOCUMENT_KEYS.RESERVATION_CONTRACT,
            user_id: selectedSourceUserId || undefined,
          },
          userInput,
          getToken,
        );
      } catch (draftErr) {
        console.warn('reservationContract: failed to save draft', draftErr.message);
      }

      const lvExtra = buildLvNumberPayload({ summary, pdfSummary, initialProperty, lvTrim });
      const payload = { xml, userInput, ...lvExtra };
      const genRes = await apiPost('/api/generate-sale-order-flat', payload, getToken);
      if (genRes.status === 403) {
        const genData = await genRes.json().catch(() => ({}));
        if (genData.code === 'SETUP_REQUIRED' && onSetupRequired) {
          onSetupRequired();
          return;
        }
        throw new Error(genData.error || t('reservationContract.errorGenerate'));
      }
      if (!genRes.ok) {
        const genData = await genRes.json().catch(() => ({}));
        throw new Error(genData.error || t('reservationContract.errorGenerate'));
      }
      const blob = await genRes.blob();
      const disposition = genRes.headers.get('Content-Disposition');
      const match = disposition && disposition.match(/filename="?([^";]+)"?/);
      const filename = match ? match[1].trim() : `REZERVACNI_SMLOUVA-${kuTrim}-${lvTrim}.docx`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setGenerateError(err.message || t('reservationContract.errorUnknown'));
    } finally {
      setGenerateLoading(false);
    }
  };

  const handleGenerateGdpr = async () => {
    if (!hasXml || gdprLoading) return;
    setGdprError(null);
    setGdprLoading(true);
    try {
      const lvExtra = buildLvNumberPayload({ summary, pdfSummary, initialProperty, lvTrim });
      const payload = { xml, ...lvExtra };
      const genRes = await apiPost('/api/generate-gdpr', payload, getToken);
      if (!genRes.ok) {
        const genData = await genRes.json().catch(() => ({}));
        throw new Error(genData.error || 'Failed to generate GDPR.');
      }
      const blob = await genRes.blob();
      const disposition = genRes.headers.get('Content-Disposition');
      const match = disposition && disposition.match(/filename="?([^";]+)"?/);
      const filename = match ? match[1].trim() : `GDPR_vyplneno-${kuTrim}-${lvTrim}.docx`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setGdprError(err.message || 'Failed to generate GDPR.');
    } finally {
      setGdprLoading(false);
    }
  };

  // PDF upload is intentionally disabled in XML-first mode.

  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center px-3 sm:px-4 py-6 sm:py-8 w-full max-w-full min-w-0">
      <div className="w-full max-w-md min-w-0">
        <div className="rounded-2xl border border-slate-200/80 bg-white shadow-lg shadow-slate-200/50 p-4 sm:p-8">
          {handoffLoading && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 mb-4">
              Načítám předvyplněný kontext z admin dashboardu…
            </div>
          )}
          {handoffContext && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800 mb-4">
              Převzetí z admin dashboardu potvrzeno. Property code: <strong>{handoffContext.property_code}</strong>
            </div>
          )}
          <h1 className="text-xl font-semibold text-slate-800 tracking-tight mb-1">{t('reservationContract.landingTitle')}</h1>
          <p className="text-slate-500 text-sm mb-8">{t('reservationContract.landingSubtitle')}</p>
          {!hasSource ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (!isAdmin || !canLoadLv) return;
                loadXmlForKuLv(kuTrim, lvTrim);
              }}
              className="space-y-5"
            >
              {savedProperties.length > 0 && (
                <div>
                  <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">{t('rentOrder.savedPropertiesLabel')}</label>
                  <select
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50/50"
                    value={selectedPropertyId}
                    onChange={async (e) => {
                      const id = e.target.value;
                      setSelectedPropertyId(id);
                      const prop = savedProperties.find((p) => String(p.id) === id);
                      if (prop) {
                        const kk = String(prop.kod_ku || '').trim();
                        const ll = String(prop.cislo_lv || '').trim();
                        setKu(kk);
                        setLv(ll);
                        if (isPdfBackedProperty(prop)) {
                          setError(null);
                          setLoadLoading(true);
                          try {
                            const { pdfBase64: b64, pdfName: name } = await fetchStoredLvPdf(getToken, {
                              kod_ku: kk,
                              cislo_lv: ll,
                              user_id: isAdmin && prop.user_id ? prop.user_id : undefined,
                            });
                            setPdfBase64(b64);
                            setPdfName(name || 'LV.pdf');
                            setXml(null);
                            setSummary(null);
                          } catch (err) {
                            setError(err.message || t('reservationContract.errorFetch'));
                          } finally {
                            setLoadLoading(false);
                          }
                        } else if (!isAdmin && kk && ll) {
                          loadXmlForKuLv(kk, ll);
                        }
                      }
                    }}
                  >
                    <option value="">{t('rentOrder.savedPropertiesPlaceholder')}</option>
                    {savedProperties.map((p) => (
                      <option key={p.id} value={p.id}>
                        {formatSavedPropertyLabel(p, t)}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {!isAdmin && (
                <p className="text-sm text-slate-600">{t('lvWorkspace.agentNoKuLvHint')}</p>
              )}
              {isAdmin && (
                <>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">{t('reservationContract.kuLabel')}</label>
                    <p className="text-xs text-slate-400 mb-1">{t('docFormHints.kuLv')}</p>
                    <input type="text" inputMode="numeric" value={ku} onChange={(e) => setKu(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder={t('reservationContract.kuPlaceholder')}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50/50" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">{t('reservationContract.lvLabel')}</label>
                    <p className="text-xs text-slate-400 mb-1">{t('docFormHints.kuLv')}</p>
                    <input type="text" inputMode="numeric" value={lv} onChange={(e) => setLv(e.target.value.replace(/\D/g, '').slice(0, 8))}
                      placeholder={t('reservationContract.lvPlaceholder')}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50/50" />
                  </div>
                </>
              )}
              {error && <div className="rounded-lg bg-red-50 border border-red-100 px-3 py-2 text-sm text-red-700">{error}</div>}
              {isAdmin && (
                <button type="submit" disabled={!canLoadLv}
                  className="w-full py-3 px-4 rounded-xl bg-slate-800 text-white font-medium text-sm disabled:opacity-50">
                  {loadLoading ? t('reservationContract.loading') : t('reservationContract.loadLv')}
                </button>
              )}
              {/* PDF upload UX temporarily disabled (XML-first mode)
              <div className="pt-2 border-t border-slate-100">
                <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                  {isAdmin ? t('common.adminPdfUploadLabel') : t('common.pdfUploadLabel')}
                </label>
                <p className="text-xs text-slate-400 mb-1">{t('docFormHints.pdfApartment')}</p>
                <input type="file" accept="application/pdf" onChange={handlePdfUpload} className="block w-full text-sm text-slate-600" />
                {pdfName && <p className="text-xs text-slate-500 mt-1">{t('common.selectedPdf')}: {pdfName}</p>}
              </div>
              */}
            </form>
          ) : (
            <form onSubmit={handleGenerate} className="space-y-4">
              {summary ? (
                <LvSummaryAll summary={summary} />
              ) : (
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                  {t('rentOrder.fromLvIntro')}
                </div>
              )}
              <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 space-y-2">
                <label className="text-xs text-slate-700 block">
                  <span className="font-medium">{t('propertyTypeOverride.detectedTypeLabel')}</span>
                  <input
                    type="text"
                    readOnly
                    value={t(`propertyTypeOverride.detectedTypeValue.${detectedPropertyType}`)}
                    className="mt-1 w-full rounded border border-slate-300 bg-slate-100 px-2 py-2 text-sm text-slate-700"
                  />
                </label>
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={overrideDetectedType}
                    onChange={(e) => setOverrideDetectedType(e.target.checked)}
                  />
                  <span>{t('propertyTypeOverride.overrideToggle')}</span>
                </label>
                {overrideDetectedType && (
                  <div>
                    <select
                      value={overrideType}
                      onChange={(e) => setOverrideType(e.target.value)}
                      className="w-full rounded border border-amber-300 bg-white px-2 py-2 text-sm"
                    >
                      <option value={PROPERTY_TYPE_VALUES.LAND}>{t('propertyTypeOverride.detectedTypeValue.land')}</option>
                      <option value={PROPERTY_TYPE_VALUES.HOUSE_OR_OTHER_BUILDING}>{t('propertyTypeOverride.detectedTypeValue.house_or_other_building')}</option>
                      <option value={PROPERTY_TYPE_VALUES.UNIT}>{t('propertyTypeOverride.detectedTypeValue.unit')}</option>
                    </select>
                    <p className="mt-2 text-[11px]">{t('propertyTypeOverride.overrideWarning')}</p>
                  </div>
                )}
              </div>
              <div className="text-sm font-medium text-slate-700">{t('reservationContract.buyerSection')}</div>
              {buyers.map((b, idx) => (
                <div key={`buyer-${idx}`} className="space-y-2 rounded-lg border border-slate-200 p-3">
                  <input value={b.buyerName} onChange={(e) => updateBuyer(idx, 'buyerName', e.target.value)} placeholder={t('reservationContract.buyerName')} className="w-full px-3 py-2 border rounded-lg" />
                  <input value={b.buyerLastName} onChange={(e) => updateBuyer(idx, 'buyerLastName', e.target.value)} placeholder={t('reservationContract.buyerLastName')} className="w-full px-3 py-2 border rounded-lg" />
                  <input value={b.buyerBirthDate} onChange={(e) => updateBuyer(idx, 'buyerBirthDate', e.target.value)} placeholder={t('reservationContract.buyerBirthDate')} className="w-full px-3 py-2 border rounded-lg" />
                  <input value={b.buyerPermanentAddress} onChange={(e) => updateBuyer(idx, 'buyerPermanentAddress', e.target.value)} placeholder={t('reservationContract.buyerPermanentAddress')} className="w-full px-3 py-2 border rounded-lg" />
                  {buyers.length > 1 && (
                    <button type="button" onClick={() => setBuyers((prev) => prev.filter((_, i) => i !== idx))} className="text-xs text-red-600">
                      {t('reservationContract.removeBuyer')}
                    </button>
                  )}
                </div>
              ))}
              <button type="button" onClick={() => setBuyers((prev) => [...prev, blankBuyer()])} className="text-sm text-slate-700">
                + {t('reservationContract.addBuyer')}
              </button>

              <div>
                <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider">{t('reservationContract.floorText')}</label>
                <p className="text-xs text-slate-400 mb-1">{t('docFormHints.floor')}</p>
                <input value={floorText} onChange={(e) => setFloorText(e.target.value)} placeholder={t('reservationContract.floorTextPlaceholder')} className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider">{t('reservationContract.sellingPrice')}</label>
                <p className="text-xs text-slate-400 mb-1">{t('docFormHints.money')}</p>
                <input value={sellingPrice} onChange={(e) => setSellingPrice(e.target.value)} placeholder={t('reservationContract.moneyPlaceholder')} className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider">{t('reservationContract.reservationFee')}</label>
                <p className="text-xs text-slate-400 mb-1">{t('docFormHints.money')}</p>
                <input value={reservationFee} onChange={(e) => setReservationFee(e.target.value)} placeholder={t('reservationContract.moneyPlaceholder')} className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider">{t('reservationContract.variableSymbol')}</label>
                <p className="text-xs text-slate-400 mb-1">{t('docFormHints.variableSymbol')}</p>
                <input value={variableSymbol} onChange={(e) => setVariableSymbol(e.target.value)} placeholder={t('reservationContract.variableSymbol')} className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider">{t('reservationContract.ownFunds')}</label>
                <p className="text-xs text-slate-400 mb-1">{t('docFormHints.money')}</p>
                <input value={ownFunds} onChange={(e) => setOwnFunds(e.target.value)} placeholder={t('reservationContract.moneyPlaceholder')} className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider">{t('reservationContract.mortgageAmount')}</label>
                <p className="text-xs text-slate-400 mb-1">{t('docFormHints.money')}</p>
                <input value={mortgageAmount} onChange={(e) => setMortgageAmount(e.target.value)} placeholder={t('reservationContract.moneyPlaceholder')} className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider">{t('reservationContract.deadlineDate')}</label>
                <p className="text-xs text-slate-400 mb-1">{t('docFormHints.dateCs')}</p>
                <input value={deadlineDate} onChange={(e) => setDeadlineDate(e.target.value)} placeholder={t('reservationContract.datePlaceholder')} className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider">{t('reservationContract.todayDate')}</label>
                <p className="text-xs text-slate-400 mb-1">{t('docFormHints.dateCs')}</p>
                <input value={todayDate} onChange={(e) => setTodayDate(e.target.value)} placeholder={t('reservationContract.datePlaceholder')} className="w-full px-3 py-2 border rounded-lg" />
              </div>

              <button type="submit" disabled={!canGenerate} className="w-full py-3 px-4 rounded-xl bg-slate-800 text-white font-medium text-sm disabled:opacity-50">
                {generateLoading ? t('reservationContract.saving') : t('reservationContract.generateDocAction')}
              </button>
              <button type="button" onClick={handleGenerateGdpr} disabled={gdprLoading || !hasXml} className="w-full py-2.5 px-4 rounded-xl border border-slate-200 text-slate-700 text-sm hover:bg-slate-50 disabled:opacity-50">
                {gdprLoading ? t('rentOrder.generatingGdpr') : t('reservationContract.generateGdprAction')}
              </button>
              <button type="button" onClick={onBack} className="w-full py-2.5 px-4 rounded-xl border border-slate-200 text-slate-600 text-sm hover:bg-slate-50 transition-colors">
                {t('reservationContract.backMainAction')}
              </button>
              {(generateError || gdprError) && <div className="rounded-lg bg-red-50 border border-red-100 px-3 py-2 text-sm text-red-700">{generateError || gdprError}</div>}
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

